/**
 * Pure occurrence-date helpers shared by frontend and backend so a multi-day
 * single-occurrence span expands into the same per-day session set everywhere.
 *
 * Dates are `yyyy-MM-dd` strings. Expansion is inclusive of both ends and uses
 * UTC math to stay timezone-stable.
 */

const MS_PER_DAY = 86_400_000;

/**
 * Every date from `startISO` to `endISO` inclusive. Returns `[startISO]` when
 * there is no later end date, and `[]` when no start date is given.
 */
export const expandDateRangeInclusive = (
  startISO: string | null | undefined,
  endISO: string | null | undefined,
): string[] => {
  if (!startISO) return [];
  if (!endISO || endISO <= startISO) return [startISO];

  const start = new Date(`${startISO}T00:00:00Z`);
  const end = new Date(`${endISO}T00:00:00Z`);
  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return [startISO];
  }

  const dates: string[] = [];
  for (let t = start.getTime(); t <= end.getTime(); t += MS_PER_DAY) {
    dates.push(new Date(t).toISOString().slice(0, 10));
  }
  return dates;
};

/** Whether a start/end pair describes a span covering more than one day. */
export const isMultiDaySpan = (
  startISO: string | null | undefined,
  endISO: string | null | undefined,
): boolean => Boolean(startISO && endISO && endISO > startISO);
